import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  TrackByFunction,
  inject,
  input,
  output,
  signal,
  computed,
  ChangeDetectorRef,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { debounceTime, distinctUntilChanged, ReplaySubject, takeUntil, tap } from 'rxjs';

import { formatUserName } from 'app/core/utilities/user-util';
import { ScootaversePageService } from 'app/modules/scootaverse/services/scootaverse-page.service';
import { notNil } from 'app/utils/stream-util';
import AlternateHost from 'entity/AlternateHost';
import User from 'entity/User';
import Paging from 'rest/Paging';

import { socialAppSelectors } from '../state/app';
import { socialHomePageActions, socialHomePageSelectors } from '../state/home-page';
import { ALTERNATE_HOST_CANDIDATES_PAGE_SIZE } from '../state/home-page/reducer';

export enum RoomIdentifierType {
  SOCIAL_ID,
  SOCIAL_ROOM_SET_ID,
}

@Component({
  selector: 'alternate-hosts-selector',
  templateUrl: './alternate-hosts-selector.component.html',
  styleUrls: ['./alternate-hosts-selector.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlternateHostsSelectorComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private scootaversePageService = inject(ScootaversePageService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  public readonly selectionChange = output<AlternateHost[]>();
  public readonly roomIdentifier = input<RoomIdentifierType>(RoomIdentifierType.SOCIAL_ID);
  public readonly showLicenseError = input(false);
  public search = new UntypedFormControl('');
  public readonly isLoading = signal<boolean>(false);

  private destroyed$ = new ReplaySubject<boolean>(1);
  private readonly _alternateHostCandidates = signal<AlternateHost[]>([]);
  private readonly selectedCandidates = signal<AlternateHost[]>([]);
  private readonly currentAlternateHosts = signal<User[]>([]);
  private readonly currentPage = signal<number>(0);
  private itemsPerPage = ALTERNATE_HOST_CANDIDATES_PAGE_SIZE;
  private readonly hasMore = signal<boolean>(false);
  private readonly socialId = signal<string | undefined>(undefined);
  private readonly socialRoomSetId = signal<string | undefined>(undefined);

  public ngOnInit(): void {
    const roomIdentifier = this.roomIdentifier();
    if (roomIdentifier === RoomIdentifierType.SOCIAL_ID) {
      this.store
        .select(socialAppSelectors.getCurrentSocialId)
        .pipe(
          tap((socialId) => this.socialId.set(socialId)),
          takeUntil(this.destroyed$),
        )
        .subscribe(() => {
          this.fetchAlternateHostCandidates();
          this.changeDetectorRef.markForCheck();
        });

      this.store
        .select(socialAppSelectors.getAlternateHosts)
        .pipe(
          tap((hosts) => this.currentAlternateHosts.set(hosts)),
          takeUntil(this.destroyed$),
        )
        .subscribe();
    }

    if (roomIdentifier === RoomIdentifierType.SOCIAL_ROOM_SET_ID) {
      this.scootaversePageService.selectedScootaverse$
        .pipe(takeUntil(this.destroyed$))
        .subscribe((selectedScootaverse) => {
          if (selectedScootaverse) {
            this.socialRoomSetId.set(selectedScootaverse.socialRoomSetId);
            this.currentAlternateHosts.set(selectedScootaverse.alternateHosts ?? []);
            this.fetchAlternateHostCandidates();
            this.changeDetectorRef.detectChanges();
          }
        });
    }

    this.store
      .select(socialHomePageSelectors.getAlternateHostCandidates)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ data, hasMore, loading, page }) => {
        this._alternateHostCandidates.set(data);
        this.currentPage.set(page);
        this.isLoading.set(loading);
        this.hasMore.set(hasMore);
      });

    this.search.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe(() => {
        this.store.dispatch(socialHomePageActions.RESET_ALTERNATE_HOSTS_CANDIDATES());
        this.fetchAlternateHostCandidates();
        this.changeDetectorRef.detectChanges();
      });

    this.fetchAlternateHostCandidates();
  }

  public readonly alternateHostCandidates = computed(() => {
    return this._alternateHostCandidates().filter(this.notSelected).filter(this.notACurrentHost);
  });

  public readonly alternateHosts = computed(() => {
    return [
      ...this.selectedCandidates().filter(this.notACurrentHost),
      ...this.currentAlternateHosts().map((user) => ({
        user,
        host: true,
        licensed: true,
      })),
    ];
  });

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public fetchMore(): void {
    if (this.hasMore()) {
      this.fetchAlternateHostCandidates(this.currentPage() + 1);
    }
  }

  public removeAlternateHost(userId?: string): void {
    const social = this.roomIdentifier() === RoomIdentifierType.SOCIAL_ID ? this.socialId() : this.socialRoomSetId();
    if (!social || !userId) {
      return;
    }

    this.selectedCandidates.set(this.selectedCandidates().filter((c) => c.user?.userId !== userId));
    this.currentAlternateHosts.set(this.currentAlternateHosts().filter((c) => c.userId !== userId));

    this.selectionChange.emit(this.alternateHosts());
    this.changeDetectorRef.detectChanges();
  }

  public selectHost(candidate: AlternateHost): void {
    const { userId } = candidate.user ?? {};
    const social = this.roomIdentifier() === RoomIdentifierType.SOCIAL_ID ? this.socialId() : this.socialRoomSetId();
    if (!social || !userId) {
      return;
    }
    const updatedCandidates = [...this.selectedCandidates(), candidate];
    this.selectedCandidates.set(updatedCandidates);

    this.selectionChange.emit(this.alternateHosts());

    // There has to be at least 3 users on the UI to have the scroll functionality working
    if (this.alternateHostCandidates().length < 3) {
      this.fetchMore();
    }
    this.changeDetectorRef.detectChanges();
  }

  public trackById: TrackByFunction<AlternateHost> = (index: number, item: AlternateHost) => item.user?.userId;

  public getUserName(user?: User): string {
    if (!user) {
      return '';
    }
    return formatUserName(user) ?? '';
  }

  private fetchAlternateHostCandidates(page?: number): void {
    const roomIdentifier = this.roomIdentifier();
    if (roomIdentifier === RoomIdentifierType.SOCIAL_ID) {
      const currentSocialId = this.socialId();
      if (!currentSocialId) {
        return;
      }
      this.store.dispatch(
        socialHomePageActions.FETCH_ALTERNATE_HOSTS_CANDIDATES_REQ({
          socialId: currentSocialId,
          paging: new Paging(page ?? 0, this.itemsPerPage),
          text: this.search.value,
        }),
      );
    }
    if (roomIdentifier === RoomIdentifierType.SOCIAL_ROOM_SET_ID) {
      const currentSocialRoomSetId = this.socialRoomSetId();
      if (!currentSocialRoomSetId) {
        return;
      }
      this.store.dispatch(
        socialHomePageActions.FETCH_ALTERNATE_HOSTS_CANDIDATES_ROOMSET_REQ({
          socialRoomSetId: currentSocialRoomSetId,
          paging: new Paging(page ?? 0, this.itemsPerPage),
          text: this.search.value,
        }),
      );
    }
  }

  private notACurrentHost = (candidate: AlternateHost) =>
    !this.currentAlternateHosts()
      .map((u: User) => u.userId)
      .includes(candidate.user?.userId ?? '');
  private notSelected = (candidate: AlternateHost) =>
    !this.selectedCandidates()
      .map((u: AlternateHost) => u.user?.userId)
      .filter(notNil)
      .includes(candidate.user?.userId ?? '');
}
